"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { FilePlus, Images, LogOut, Mail } from "lucide-react";
import { canCreateGalleryContent } from "@/lib/galleryAccess";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
	subscribeNewsletter,
	unsubscribeNewsletter,
} from "@/lib/newsletterPreference";

type User = {
	id: string;
	email: string;
	name: string;
	role: "user" | "admin" | "staff";
	section?: string | null;
	newsletterSubscribed?: boolean;
};

type Props = {
	user: User;
	handleLogout: () => void;
	handleDeleteAccount: () => void | Promise<void>;
	onUserChange?: (user: User) => void;
};

export default function AuthenticatedUser({
	user,
	handleLogout,
	handleDeleteAccount,
}: Props) {
	const [deleting, setDeleting] = useState(false);
	const [newsletterPending, setNewsletterPending] = useState(false);
	const [subscribed, setSubscribed] = useState(
		Boolean(user.newsletterSubscribed),
	);
	const displayName = user.name?.trim() || user.email;
	const isAdmin = user.role === "admin";

	useEffect(() => {
		setSubscribed(Boolean(user.newsletterSubscribed));
	}, [user.newsletterSubscribed]);

	const onConfirmDelete = async () => {
		setDeleting(true);
		try {
			await handleDeleteAccount();
		} finally {
			setDeleting(false);
		}
	};

	const handleSubscribe = async () => {
		if (newsletterPending || subscribed) return;
		setNewsletterPending(true);
		try {
			const result = await subscribeNewsletter();
			if (!result.ok) {
				toast.error(result.error || "Iscrizione non riuscita");
				return;
			}
			setSubscribed(result.subscribed);
			toast.success(
				"Iscrizione alla newsletter effettuata. Controlla la tua email.",
			);
		} catch {
			toast.error("Errore di connessione");
		} finally {
			setNewsletterPending(false);
		}
	};

	const handleUnsubscribe = async () => {
		if (newsletterPending || !subscribed) return;
		setNewsletterPending(true);
		try {
			const result = await unsubscribeNewsletter();
			if (!result.ok) {
				toast.error(result.error || "Disiscrizione non riuscita");
				return;
			}
			setSubscribed(result.subscribed);
			toast.success(
				"Iscrizione annullata. Ti abbiamo inviato una conferma via email.",
			);
		} catch {
			toast.error("Errore di connessione");
		} finally {
			setNewsletterPending(false);
		}
	};

	return (
		<div className="md:w-1/2 w-full p-8 md:p-16 flex flex-col items-center justify-center">
			<div className="w-full max-w-md mx-auto">
				<h2 className="text-3xl font-bold mb-2">
					<span className="text-(--foreground)">Ciao, </span>
					<span className="text-(--card-foreground)">{displayName}!</span>
				</h2>

				<p className="text-sm text-gray-500 mb-6">
					Sei connesso all&apos;area riservata
					{!isAdmin && user.section ? (
						<>
							{" "}
							· Sezione{" "}
							<span className="font-medium text-(--foreground)">
								{user.section}
							</span>
						</>
					) : null}
				</p>

				<div className="flex flex-col gap-3">
					{user.section ? (
						<Button
							asChild
							className="w-full h-11 cursor-pointer bg-(--btn-bg) text-(--btn-foreground) font-semibold text-md rounded-lg px-6 shadow-md transition-all duration-300 hover:bg-(--btn-hover) hover:shadow-lg"
						>
							<Link href="/area-riservata/gallery">
								<Images className="size-4" />
								Gallery
							</Link>
						</Button>
					) : null}

					{canCreateGalleryContent(user) ? (
						<Button
							asChild
							className="w-full h-11 cursor-pointer bg-(--btn-bg) text-(--btn-foreground) font-semibold text-md rounded-lg px-6 shadow-md transition-all duration-300 hover:bg-(--btn-hover) hover:shadow-lg"
						>
							<Link href="/admin/gallery/create-activity">
								<FilePlus className="size-4" />
								Crea Attività
							</Link>
						</Button>
					) : null}

					<div className="rounded-lg border border-(--card-foreground)/20 bg-[#f0f8ff]/50 px-4 py-3 flex flex-col gap-2">
						<p className="text-sm font-semibold text-(--foreground) flex items-center gap-2">
							<Mail className="size-4 text-(--card-foreground)" />
							Newsletter
						</p>
						{subscribed ? (
							<>
								<p className="text-sm text-muted-foreground">
									Sei iscritto agli aggiornamenti della scuola.
								</p>
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<button
											type="button"
											disabled={newsletterPending}
											className="self-start text-sm text-muted-foreground underline-offset-4 hover:text-red-600 hover:underline cursor-pointer transition-colors disabled:opacity-50"
										>
											Disiscriviti dalla newsletter
										</button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												Disiscriverti dalla newsletter?
											</AlertDialogTitle>
											<AlertDialogDescription>
												Non riceverai più aggiornamenti su circolari, eventi e
												novità della scuola. Potrai iscriverti di nuovo in
												qualsiasi momento.
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel className="cursor-pointer">
												Annulla
											</AlertDialogCancel>
											<AlertDialogAction
												disabled={newsletterPending}
												onClick={handleUnsubscribe}
												className="cursor-pointer bg-red-600 text-white hover:bg-red-700"
											>
												{newsletterPending
													? "Attendere…"
													: "Conferma disiscrizione"}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</>
						) : (
							<>
								<p className="text-sm text-muted-foreground">
									Non sei iscritto alla newsletter.
								</p>
								<Button
									type="button"
									disabled={newsletterPending}
									onClick={handleSubscribe}
									className="w-full h-10 cursor-pointer rounded-lg bg-(--btn-bg) text-(--btn-foreground) font-semibold hover:bg-(--btn-hover)"
								>
									{newsletterPending ? "Attendere…" : "Iscriviti"}
								</Button>
							</>
						)}
					</div>

					<Button
						type="button"
						variant="outline"
						onClick={handleLogout}
						className="w-full h-11 cursor-pointer rounded-lg border-(--card-foreground)/40 bg-white text-(--card-foreground) font-semibold text-md px-6 transition-all hover:bg-[#f0f8ff] hover:text-(--card-foreground) hover:border-(--card-foreground)"
					>
						<LogOut className="size-4" />
						Esci
					</Button>

					<AlertDialog>
						<AlertDialogTrigger asChild>
							<button
								type="button"
								className="mt-2 self-center text-sm text-muted-foreground underline-offset-4 hover:text-red-600 hover:underline cursor-pointer transition-colors"
							>
								Elimina account
							</button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Eliminare l&apos;account?</AlertDialogTitle>
								<AlertDialogDescription>
									Questa azione è permanente. Perderai l&apos;accesso all&apos;area
									riservata e ai contenuti collegati al tuo profilo.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel className="cursor-pointer">
									Annulla
								</AlertDialogCancel>
								<AlertDialogAction
									disabled={deleting}
									onClick={onConfirmDelete}
									className="cursor-pointer bg-red-600 text-white hover:bg-red-700"
								>
									{deleting ? "Eliminazione…" : "Elimina definitivamente"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>
		</div>
	);
}
